import React from 'react';

// Matches **bold** spans — the one piece of markdown Ria's responses have
// ever used on web. Kept as its own pass (rather than pulled into a full
// markdown library) so every existing message that doesn't contain a table
// renders through exactly this same regex, byte-for-byte as before.
const BOLD_PATTERN = /(\*\*.*?\*\*)/g;

const formatInlineBold = (text, keyPrefix) => {
  const parts = text.split(BOLD_PATTERN);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

// A GFM-style separator row, e.g. "|---|:---:|---|" or "| --- | --- |".
const isTableSeparatorLine = (line) =>
  /^\s*\|?(\s*:?-{2,}:?\s*\|)+\s*:?-{2,}:?\s*\|?\s*$/.test(line.trim());

const looksLikeTableRow = (line) => {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.length > 1;
};

const parseTableRow = (line) =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim());

/**
 * Splits Ria's response into plain-text and markdown-table segments. Each
 * table segment (a header row, a "|---|---|" separator row, then 1+ data
 * rows) renders as a real <table>; everything else — including a message
 * with no table at all — goes through the same **bold**-only formatting
 * that this function has always done.
 *
 * Deliberately not a general markdown parser (no headers/links/italics/
 * nested lists): extending the existing minimal formatter instead of
 * swapping in a markdown library means a message that doesn't contain a
 * table renders identically to before this function existed.
 */
export const formatRiaMessage = (text) => {
  if (!text) return text;

  const lines = text.split('\n');
  const blocks = [];
  let currentText = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const nextLine = lines[i + 1];
    const isTableStart = looksLikeTableRow(line) && nextLine != null && isTableSeparatorLine(nextLine);

    if (isTableStart) {
      if (currentText.length) {
        blocks.push({ type: 'text', content: currentText.join('\n') });
        currentText = [];
      }
      const header = parseTableRow(line);
      i += 2; // skip the header row and the separator row
      const rows = [];
      while (i < lines.length && looksLikeTableRow(lines[i])) {
        rows.push(parseTableRow(lines[i]));
        i += 1;
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }

    currentText.push(line);
    i += 1;
  }
  if (currentText.length) {
    blocks.push({ type: 'text', content: currentText.join('\n') });
  }

  // Fast path — no table anywhere in the message: return the exact same
  // shape (an array of strings / <strong> elements, nothing else wrapping
  // them) that this function has always returned.
  if (blocks.length === 1 && blocks[0].type === 'text') {
    return formatInlineBold(blocks[0].content, 'b0');
  }

  return blocks.map((block, idx) => {
    if (block.type === 'table') {
      return (
        <table className="ria-response-table" key={`table-${idx}`}>
          <thead>
            <tr>
              {block.header.map((cell, ci) => (
                <th key={ci}>{formatInlineBold(cell, `th-${idx}-${ci}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci}>{formatInlineBold(cell, `td-${idx}-${ri}-${ci}`)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    return <React.Fragment key={`text-${idx}`}>{formatInlineBold(block.content, `t${idx}`)}</React.Fragment>;
  });
};
